// HPLC method transfer: geometrically scale a method between two columns while
// keeping the separation equivalent. Flow scales with cross-sectional area and
// particle size; injection volume and isocratic/gradient time scale with column
// volume and flow.

export interface HplcColumn {
  /** internal diameter, mm */
  id: number;
  /** column length, mm */
  length: number;
  /** particle size, µm */
  particle: number;
}

export interface HplcTransfer {
  flowFactor: number;
  /** transferred flow rate, mL/min */
  flow: number;
  /** transferred injection volume, µL */
  injection: number;
  /** transferred gradient/run time, min */
  gradientTime: number;
  /** column volume ratio (new ÷ original) */
  volumeRatio: number;
}

/**
 * Transfer a method from `from` to `to`. `flow`, `injection`, `gradientTime`
 * are the ORIGINAL method values. Returns null on invalid geometry.
 */
export function transferMethod(
  from: HplcColumn,
  to: HplcColumn,
  flow: number,
  injection: number,
  gradientTime: number,
): HplcTransfer | null {
  const geom = [
    from.id,
    from.length,
    from.particle,
    to.id,
    to.length,
    to.particle,
  ];
  if (geom.some((v) => !Number.isFinite(v) || v <= 0)) return null;
  if (
    ![flow, injection, gradientTime].every((v) => Number.isFinite(v) && v >= 0)
  )
    return null;

  // Flow scales with (id²) and inversely with particle size (constant linear velocity).
  const flowFactor = (to.id / from.id) ** 2 * (from.particle / to.particle);
  const newFlow = flow * flowFactor;

  // Column volume ∝ id² × length.
  const volFrom = from.id ** 2 * from.length;
  const volTo = to.id ** 2 * to.length;
  const volumeRatio = volTo / volFrom;

  // Injection volume scales with column volume.
  const newInjection = injection * volumeRatio;

  // Gradient time scales with column volume ÷ flow-rate ratio.
  const newGradient = gradientTime * (volumeRatio / flowFactor);

  return {
    flowFactor,
    flow: newFlow,
    injection: newInjection,
    gradientTime: newGradient,
    volumeRatio,
  };
}
